import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal, SectionLabel, Counter } from "@/components/site/primitives";
import { Lightbox } from "@/components/site/Lightbox";
import { ACTIVITIES, ACTIVITY_GALLERY, ACTIVITY_STATS } from "@/data/content";

const ActivityCard = ({ item, onOpen }) => (
  <Reveal>
    <article
      data-testid={`activity-${item.id}`}
      className="group h-full flex flex-col border border-raritan-border bg-raritan-surface hover:bg-raritan-green-soft transition-colors duration-500"
    >
      <button
        type="button"
        onClick={onOpen}
        className="relative w-full aspect-[4/3] overflow-hidden bg-raritan-green-soft cursor-zoom-in"
      >
        <img
          src={item.image}
          alt={item.imageAlt}
          className="w-full h-full object-contain"
        />
        <span className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-raritan-ink flex items-center gap-2 shadow-sm whitespace-nowrap">
          <span className={`w-1.5 h-1.5 ${item.accent === "red" ? "bg-raritan-red" : "bg-raritan-green"}`} />
          {item.tag}
        </span>
      </button>
      <div className="p-6 md:p-7 flex-1 flex flex-col">
        <div className="flex items-baseline gap-3 mb-3">
          <span className="font-heading font-black text-3xl text-raritan-red/25 leading-none">{item.id}</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-raritan-muted">— Activity</span>
        </div>
        <h3 className="font-heading font-bold text-xl md:text-2xl tracking-tight text-raritan-ink">{item.title}</h3>
        <p className="mt-2 font-serif italic text-raritan-green">{item.lead}</p>
        <p className="mt-4 font-body text-sm text-raritan-muted leading-relaxed flex-1">{item.desc}</p>
        <ul className="mt-5 pt-4 border-t border-raritan-border space-y-1.5">
          {item.highlights.map((h) => (
            <li key={h} className="flex items-start gap-2 font-body text-xs text-raritan-ink">
              <Sparkles size={12} className={item.accent === "red" ? "text-raritan-red mt-0.5 shrink-0" : "text-raritan-green mt-0.5 shrink-0"} />
              {h}
            </li>
          ))}
        </ul>
      </div>
    </article>
  </Reveal>
);

const Stats = () => (
  <section data-testid="activity-stats" className="px-5 md:px-10 py-16 bg-raritan-green text-white">
    <div className="max-w-[1400px] mx-auto grid grid-cols-2 lg:grid-cols-4 gap-8">
      {ACTIVITY_STATS.map((s, i) => (
        <Reveal key={s.label} delay={i * 0.08}>
          <div className="border-t-2 border-raritan-red pt-4">
            <Counter value={s.value} suffix={s.suffix} className="font-heading font-black text-4xl md:text-6xl tracking-tighter block" />
            <p className="mt-2 font-body text-xs md:text-sm text-white/70">{s.label}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const CTA = () => (
  <section className="px-5 md:px-10 py-16 md:py-24">
    <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-8 items-center">
      <div className="lg:col-span-8">
        <SectionLabel className="mb-5">Partner with us</SectionLabel>
        <h2 className="font-heading font-black uppercase tracking-tighter text-3xl md:text-5xl text-raritan-ink leading-[0.95]">
          Bring these activities<br />to <span className="text-raritan-red">your city</span>
        </h2>
      </div>
      <div className="lg:col-span-4 lg:justify-self-end">
        <Link
          to="/contact"
          data-testid="activities-cta"
          className="inline-flex items-center gap-3 bg-raritan-green text-white px-7 py-4 font-mono text-xs uppercase tracking-[0.15em] hover:bg-raritan-green-hover transition-colors group"
        >
          Start a program
          <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </Link>
      </div>
    </div>
  </section>
);

export default function Activities() {
  const [cardLightbox, setCardLightbox] = useState(-1);
  const [galleryLightbox, setGalleryLightbox] = useState(-1);

  const cardItems = ACTIVITIES.map((a) => ({ src: a.image, caption: a.title }));

  return (
    <div data-testid="activities-page">
      <PageHero
        label="Activities · Community & IEC"
        titleLines={["What we do,", "on the", "ground"]}
        intro="Beyond consultancy, Raritan Consulting runs a year-round calendar of community activities across every ULB we work with — rallies, school programs, workshops, plantations, and health camps."
        testId="activities-hero"
      />

      {/* Stacked activity grid */}
      <section className="px-5 md:px-10 py-16 md:py-20 border-b border-raritan-border">
        <div className="max-w-[1400px] mx-auto">
          <SectionLabel className="mb-6">Six activity streams</SectionLabel>
          <h2 className="font-heading font-black uppercase tracking-tighter text-3xl md:text-5xl text-raritan-ink leading-[0.95] mb-12">
            The calendar,<br />on the ground
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {ACTIVITIES.map((a, i) => (
              <ActivityCard key={a.id} item={a} onOpen={() => setCardLightbox(i)} />
            ))}
          </div>
        </div>
      </section>

      <Stats />

      {/* Compact gallery */}
      <section data-testid="activity-gallery" className="px-5 md:px-10 py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
            <div>
              <SectionLabel className="mb-5">In the field</SectionLabel>
              <h2 className="font-heading font-black uppercase tracking-tighter text-3xl md:text-5xl text-raritan-ink leading-[0.95]">
                Moments,<br />documented
              </h2>
            </div>
            <p className="max-w-sm font-body text-sm text-raritan-muted leading-relaxed">
              Click any photo to view it in full. Every image is shown uncropped.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {ACTIVITY_GALLERY.map((g, i) => (
              <Reveal key={g.src + i} delay={(i % 8) * 0.04}>
                <button
                  type="button"
                  onClick={() => setGalleryLightbox(i)}
                  className="relative w-full aspect-square overflow-hidden border border-raritan-border bg-raritan-green-soft group cursor-zoom-in block"
                >
                  <img
                    src={g.src}
                    alt={g.caption}
                    className="w-full h-full object-contain"
                  />
                  <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/80 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <p className="text-white font-mono text-[10px] uppercase tracking-[0.15em] leading-tight text-left">{g.caption}</p>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTA />

      <Lightbox
        items={cardItems}
        index={cardLightbox}
        onClose={() => setCardLightbox(-1)}
        onIndexChange={setCardLightbox}
      />
      <Lightbox
        items={ACTIVITY_GALLERY}
        index={galleryLightbox}
        onClose={() => setGalleryLightbox(-1)}
        onIndexChange={setGalleryLightbox}
      />
    </div>
  );
}
